import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { fetchAllFeeds, fetchPriorityFeeds, filterByCategory, searchArticles as searchFn, wakeUpBackend } from '../services/rssService';
import { getPublishedAdminArticles, getBreakingArticles, getFeaturedArticles, fetchPublishedArticlesFromBackend } from '../services/adminService';
import { useLang } from './LanguageContext';
import { AUTO_REFRESH_INTERVAL, ARTICLES_KEY } from '../utils/constants';
import { isValidArticle } from '../utils/isValidArticle';

const NewsContext = createContext();

export const useNews = () => {
  const context = useContext(NewsContext);
  if (!context) throw new Error('useNews must be used within NewsProvider');
  return context;
};

/** Merge incoming articles into existing ones — always keeps the most recently updated version */
const mergeArticles = (existing, incoming) => {
  const map = new Map(existing.map(a => [a.id, a]));
  incoming.forEach(a => {
    const current = map.get(a.id);
    if (!current) {
      map.set(a.id, a);
      return;
    }
    const currentTime = new Date(current.updatedAt || current.pubDate || 0).getTime();
    const incomingTime = new Date(a.updatedAt || a.pubDate || 0).getTime();
    // Only overwrite if incoming is strictly newer — never let stale backend data
    // overwrite a fresh local edit that hasn't synced to backend yet
    if (incomingTime >= currentTime) {
      map.set(a.id, a);
    }
  });
  const merged = Array.from(map.values());
  merged.sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));
  return merged;
};

/** Re-read published admin articles from localStorage and merge into state */
const reloadAdminArticles = (setRawArticles, setLoading) => {
  const adminArticles = getPublishedAdminArticles();
  if (adminArticles.length > 0) {
    setRawArticles(prev => mergeArticles(prev, adminArticles));
    setLoading(false);
  }
};

export const NewsProvider = ({ children }) => {
  const [rawArticles, setRawArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedsComplete, setFeedsComplete] = useState(false);
  const [error, setError] = useState(null);
  const { lang } = useLang();

  const fetchNews = useCallback(async (force = false) => {
    setError(null);
    setFeedsComplete(false);

    // Admin articles are always visible immediately — never gated on RSS feeds loading
    reloadAdminArticles(setRawArticles, setLoading);

    // Merge published backend articles in background — never blocks rendering.
    // After backend merge, re-apply local admin articles so any fresh edits
    // (whose backend sync may not have completed yet) always take final precedence.
    fetchPublishedArticlesFromBackend().then(backendArticles => {
      if (backendArticles.length > 0) {
        setRawArticles(prev => mergeArticles(prev, backendArticles));
      }
      // Safety pass: local admin edits win over anything the backend returned
      reloadAdminArticles(setRawArticles, setLoading);
    }).catch(() => {
      reloadAdminArticles(setRawArticles, setLoading);
    });

    try {
      // Step 1: priority / cached feeds — fast
      const priorityArticles = await fetchPriorityFeeds();
      if (priorityArticles.length > 0) {
        setRawArticles(prev => mergeArticles(prev, priorityArticles));
      }
      setLoading(false);

      // Step 2: remaining feeds — background, never clears existing content
      const allRss = await fetchAllFeeds(force);
      if (allRss.length > 0) {
        setRawArticles(prev => mergeArticles(prev, allRss));
      }
      setFeedsComplete(true);
    } catch (err) {
      console.error('Feed fetch error:', err);
      setError('कुछ समाचार स्रोतों से कनेक्ट नहीं हो पाया।');
      setLoading(false);
      setFeedsComplete(true);
    }
  }, []);

  useEffect(() => {
    // Wake up Render backend immediately (free tier cold-starts in 30-45s)
    wakeUpBackend();
    fetchNews(false);
    // Single warmup refresh once Render backend is warm (~45s cold start)
    const warmupTimer = setTimeout(() => fetchNews(true), 45000);
    const interval = setInterval(() => fetchNews(true), AUTO_REFRESH_INTERVAL);
    return () => {
      clearTimeout(warmupTimer);
      clearInterval(interval);
    };
  }, [fetchNews]);

  // React instantly when admin publishes in the SAME browser tab
  useEffect(() => {
    const handleSameTab = () => reloadAdminArticles(setRawArticles, setLoading);
    window.addEventListener('adminArticlesUpdated', handleSameTab);
    return () => window.removeEventListener('adminArticlesUpdated', handleSameTab);
  }, []);

  // React instantly when admin publishes in a DIFFERENT browser tab (same origin)
  useEffect(() => {
    const handleOtherTab = (e) => {
      if (e.key === ARTICLES_KEY || e.key === null) {
        reloadAdminArticles(setRawArticles, setLoading);
      }
    };
    window.addEventListener('storage', handleOtherTab);
    return () => window.removeEventListener('storage', handleOtherTab);
  }, []);

  // All articles — RSS and admin — are filtered by the user's selected language.
  // Admin articles without an explicit lang default to 'hi' (Hindi-first portal).
  const allArticles = useMemo(() => {
    const preferredLang = lang === 'EN' ? 'en' : 'hi';
    return rawArticles
      .filter(a => (a.lang || 'hi') === preferredLang)
      .filter(isValidArticle)
      .sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));
  }, [rawArticles, lang]);

  const getBreakingNews = useCallback(() => {
    const preferredLang = lang === 'EN' ? 'en' : 'hi';
    const adminBreaking = getBreakingArticles()
      .filter(a => (a.lang || 'hi') === preferredLang);
    if (adminBreaking.length > 0) return adminBreaking;
    const breaking = allArticles.filter(a => a.breaking);
    return breaking.length > 0 ? breaking.slice(0, 5) : allArticles.slice(0, 5);
  }, [allArticles, lang]);

  const getFeatured = useCallback(() => {
    const preferredLang = lang === 'EN' ? 'en' : 'hi';
    const adminFeatured = getFeaturedArticles()
      .filter(a => (a.lang || 'hi') === preferredLang);
    if (adminFeatured.length > 0) return adminFeatured;
    const featured = allArticles.filter(a => a.featured);
    if (featured.length >= 3) return featured.slice(0, 5);
    return allArticles.slice(0, 5);
  }, [allArticles, lang]);

  const getByCategory = useCallback((cat) => {
    return filterByCategory(allArticles, cat);
  }, [allArticles]);

  const getBySource = useCallback((sources) => {
    return allArticles.filter(a => sources.includes(a.source));
  }, [allArticles]);

  const searchNews = useCallback((query) => {
    return searchFn(allArticles, query);
  }, [allArticles]);

  // Search across ALL articles regardless of active language toggle.
  // Used only by search UI — every other feature still uses allArticles (language-filtered).
  const searchAllArticles = useCallback((query) => {
    return searchFn(rawArticles.filter(isValidArticle), query);
  }, [rawArticles]);

  const getArticleById = useCallback((id) => {
    // No lang filter — direct URL navigation must always resolve regardless of current toggle
    return rawArticles.find(a => a.id === id && isValidArticle(a)) || null;
  }, [rawArticles]);

  // Returns category-filtered articles from allArticles; falls back to rawArticles
  // (same language, same category) — never returns unrelated articles.
  const getCategoryWithFallback = useCallback((cat) => {
    const primary = filterByCategory(allArticles, cat);
    if (primary.length > 0) return primary;
    const preferredLang = lang === 'EN' ? 'en' : 'hi';
    const normalizedCat = (cat || '').toLowerCase();
    return rawArticles.filter(
      a => a.category && a.category.toLowerCase() === normalizedCat && a.lang === preferredLang && isValidArticle(a)
    );
  }, [allArticles, rawArticles, lang]);

  const value = useMemo(() => ({
    allArticles,
    rawArticles,
    loading,
    feedsComplete,
    error,
    fetchNews,
    getBreakingNews,
    getFeatured,
    getByCategory,
    getCategoryWithFallback,
    getBySource,
    searchNews,
    searchAllArticles,
    getArticleById,
  }), [allArticles, rawArticles, loading, feedsComplete, error, fetchNews, getBreakingNews, getFeatured, getByCategory, getCategoryWithFallback, getBySource, searchNews, searchAllArticles, getArticleById]);

  return (
    <NewsContext.Provider value={value}>
      {children}
    </NewsContext.Provider>
  );
};
